import type {AvmSpecSegment} from "../reva-spec";
import {SEGMENT} from "../reva-spec";

export interface TimelineDataItem {
    start_of_period: string;
    price?: number | null;
    price_low?: number | null;
    price_high?: number | null;
    price_offer?: number | null;
    price_offer_low?: number | null;
    price_offer_high?: number | null;
}

export interface AggregatedTimelineValue {
    startOfPeriod: string;
    year: number;
    indexLow: number | null;
    index: number | null;
    indexHigh: number | null;
    valueLow: number | null;
    value: number | null;
    valueHigh: number | null;
}

/** The one timeline segment whose sale-price fields (`price*`) hold rent data instead — timelines report it under `price_offer*`. */
const RENT_TIMELINE_SEGMENT = SEGMENT.WHG_M;

/**
 * The region levels a timeline can be reported for — the names behind the LocationInformation API's
 * numeric `regional_level` codes.
 */
export const TimelineRegionalLevel = {
    NEIGHBORHOOD: "NEIGHBORHOOD",
    POSTCODE: "POSTCODE",
    MUNICIPALITY: "MUNICIPALITY",
    DISTRICT: "DISTRICT",
    REGION: "REGION",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type TimelineRegionalLevel = (typeof TimelineRegionalLevel)[keyof typeof TimelineRegionalLevel];

/** All region levels, in the order they are offered for selection (finest first). */
export const TIMELINE_REGIONAL_LEVEL_VALUES: TimelineRegionalLevel[] = Object.values(TimelineRegionalLevel);

/** Whether a timeline reports sale prices or rents. */
export const TimelineSegmentType = {
    SALE: "sale",
    RENT: "rent",
} as const;

// See the note on TimelineRegionalLevel above.
export type TimelineSegmentType = (typeof TimelineSegmentType)[keyof typeof TimelineSegmentType];

/** Maps the numeric `regional_level` code from the LocationInformation API to its name. */
export const REGIONAL_LEVEL_BY_CODE: Record<number, TimelineRegionalLevel> = {
    1: "NEIGHBORHOOD",
    2: "POSTCODE",
    3: "MUNICIPALITY",
    4: "DISTRICT",
    5: "REGION",
};

/** Every AVM segment except `GRST_K`: a plot has no price timeline. */
export type TimelineSegment = Exclude<AvmSpecSegment, typeof SEGMENT.GRST_K>;

/**
 * The object types a timeline can be shown for: the "Kauf" segments. `WHG_M`/`BUERO_M` are rent
 * segments and therefore never an object type of their own — they are the `rent` side of one.
 */
export type TimelineObjectType = Exclude<TimelineSegment, typeof SEGMENT.WHG_M | typeof SEGMENT.BUERO_M>;

/** Which timeline segment (sale/rent) applies to each object type. Every object type has a sale segment; only some have a rent segment. */
export const SEGMENT_BY_OBJECT_TYPE: Record<TimelineObjectType, {sale: TimelineSegment; rent: TimelineSegment | null}> =
    {
        [SEGMENT.WHG_K]: {
            sale: SEGMENT.WHG_K,
            rent: SEGMENT.WHG_M,
        },
        [SEGMENT.EZFH_K]: {
            sale: SEGMENT.EZFH_K,
            rent: null,
        },
        [SEGMENT.MFHWGH_K]: {
            sale: SEGMENT.MFHWGH_K,
            rent: SEGMENT.WHG_M,
        },
        [SEGMENT.BUERO_K]: {
            sale: SEGMENT.BUERO_K,
            rent: SEGMENT.BUERO_M,
        },
    };

function sortByStartOfPeriodAsc(a: TimelineDataItem, b: TimelineDataItem): number {
    if (a.start_of_period < b.start_of_period) return -1;
    if (a.start_of_period > b.start_of_period) return 1;
    return 0;
}

function toIndexed(value: number | null | undefined, indexBase: number | null | undefined): number | null {
    if (value == null || indexBase == null) return null;
    const index = (value * 100) / indexBase;
    return Number.isNaN(index) ? null : index;
}

/** The segments a timeline can be reported for, derived from {@link SEGMENT_BY_OBJECT_TYPE}. */
export const TIMELINE_SEGMENT_VALUES: TimelineSegment[] = Object.values(SEGMENT_BY_OBJECT_TYPE)
    .flatMap((s) => [s.sale, s.rent])
    .filter((s) => s != null);

/**
 * Aggregate one segment's raw timeline items: sorted ascending by period and indexed (=100) to the
 * earliest period's value.
 */
export function aggregateTimelineSegmentData(
    timelineValues: TimelineDataItem[],
    segment: string,
): AggregatedTimelineValue[] {
    if (!timelineValues || !Array.isArray(timelineValues) || !timelineValues.length) return [];
    const sorted = [...timelineValues].sort(sortByStartOfPeriodAsc);
    const isRent = segment === RENT_TIMELINE_SEGMENT;
    const resolve = (value?: number | null, offerValue?: number | null): number | null =>
        (isRent ? offerValue : value) ?? null;
    const resolveValue = (item: TimelineDataItem) => resolve(item.price, item.price_offer);
    const resolveValueLow = (item: TimelineDataItem) => resolve(item.price_low, item.price_offer_low);
    const resolveValueHigh = (item: TimelineDataItem) => resolve(item.price_high, item.price_offer_high);
    const indexBase = resolveValue(sorted[0]) || 1;

    return sorted.map((item) => {
        const value = resolveValue(item);
        const valueLow = resolveValueLow(item);
        const valueHigh = resolveValueHigh(item);
        return {
            startOfPeriod: item.start_of_period,
            year: new Date(item.start_of_period).getFullYear(),
            valueLow,
            value,
            valueHigh,
            indexLow: toIndexed(valueLow, indexBase),
            index: toIndexed(value, indexBase),
            indexHigh: toIndexed(valueHigh, indexBase),
        };
    });
}

/** One `timelines_trend` entry of the LocationInformation timelines response. */
export interface TimelineTrendItem {
    data: TimelineDataItem[];
    segment?: string;
    regional_level?: number;
}

/** The timelines response of the LocationInformation API. */
export interface LocationInfoTimelinesData {
    info_hint?: string;
    info_hint_de?: string;
    timelines_trend: TimelineTrendItem[];
}

export type AggregatedTimelineSegmentData = {[Key in TimelineRegionalLevel]?: AggregatedTimelineValue[]};

export type AggregatedTimelineData = {[Key in TimelineSegment]?: AggregatedTimelineSegmentData};

/**
 * Aggregate a whole timelines response into a segment/regional-level map.
 *
 * Trend items whose `segment` or `regional_level` is not one REVA knows are dropped with a
 * warning — the map is only ever read through {@link SEGMENT_BY_OBJECT_TYPE}, so an unknown key
 * would be dead weight.
 */
export function aggregateTimelineData(
    timelineData: LocationInfoTimelinesData | null | undefined,
): AggregatedTimelineData {
    const result: AggregatedTimelineData = {};

    if (!timelineData || !Array.isArray(timelineData.timelines_trend)) {
        return result;
    }

    for (const item of timelineData.timelines_trend) {
        const {regional_level, segment, data} = item;
        const regionalLevel =
            regional_level && regional_level in REGIONAL_LEVEL_BY_CODE ? REGIONAL_LEVEL_BY_CODE[regional_level] : null;
        if (!regionalLevel) {
            console.warn(`Invalid timeline item to aggregate: regional_level = ${regional_level}`, item);
            continue;
        }
        const segmentValue =
            segment && TIMELINE_SEGMENT_VALUES.includes(segment as TimelineSegment)
                ? (segment as TimelineSegment)
                : null;
        if (!segmentValue) {
            console.warn(`Invalid timeline item to aggregate: segment = ${segment}`, item);
            continue;
        }
        // An item with an empty `data` array is kept as an empty list, not dropped: consumers
        // distinguish "segment/level not reported" (key absent) from "reported, no values" (key
        // present, empty).
        if (!result[segmentValue]) result[segmentValue] = {};
        result[segmentValue][regionalLevel] = aggregateTimelineSegmentData(data, segmentValue);
    }

    return result;
}

export interface FormattedTimelineRow {
    year: string;
    value: string;
    valueLow: string;
    valueHigh: string;
    valueRange: string;
    index: string;
    indexLow: string;
    indexHigh: string;
    indexRange: string;
}

/**
 * Format an aggregated timeline for display, given the caller's number-formatting functions.
 * A range is only rendered when at least one of its bounds formats to a non-empty string.
 */
export function formatTimeline(
    timeline: AggregatedTimelineValue[] | null | undefined,
    formatValue: (value: number | null, noFraction?: boolean) => string,
    formatIndex: (value: number | null) => string,
    noFraction?: boolean,
): FormattedTimelineRow[] {
    if (!timeline?.length) return [];
    return timeline.map((v) => {
        const valueLow = formatValue(v.valueLow, noFraction);
        const valueHigh = formatValue(v.valueHigh, noFraction);
        const indexLow = formatIndex(v.indexLow);
        const indexHigh = formatIndex(v.indexHigh);
        return {
            year: v.year.toFixed(0),
            value: formatValue(v.value, noFraction),
            valueLow,
            valueHigh,
            valueRange: valueLow || valueHigh ? `${valueLow} - ${valueHigh}` : "",
            index: formatIndex(v.index),
            indexLow,
            indexHigh,
            indexRange: indexLow || indexHigh ? `${indexLow} - ${indexHigh}` : "",
        };
    });
}
